import * as yup from 'yup';
import { OPTIONAL_SERVICE_COD } from '../../../../constants/data/registerShipmentData';
import {
	isIcelandicShipmentPhone,
	isShipmentPhoneMissingInternationalPrefix,
	isValidIcelandicShipmentPhone,
	isValidShipmentPhone,
} from '../../../../helpers/formHelpers/formHelpers';
import { IFormSelectOption } from '../../../../interfaces/forms/forms';
import { OPTIONAL_SERVICE_INSURANCE } from './../../../../constants/data/registerShipmentData';

export interface IStepShipmentFormInfo {
	service: '' | 'Letter' | 'Parcel';
	optionalServices: string[];
	optionalServicesNames: any[];
	parcelCount: number;
	parcelWeight: number;
	weightCategory: string;
	serviceOption: string;
	serviceName: string;
	serviceOptionPostbox: IFormSelectOption;
	serviceOptionParcelpoint: IFormSelectOption;
	parcelWidth: string;
	parcelHeight: string;
	parcelLength: string;
	dontKnowWeight: boolean;
	price: string;

	codAmount: string;
	codSsn: string;
	codBank: string;
	codAccountType: string;
	codAccount: string;
	codReference: string;
	insuranceAmount: string;
	insuranceTerms: boolean;
	receiverPhone: string;
	receiverPhoneCountryCode: string;
	senderSsnValid: boolean;
}

export const stepShipmentInitialValues: IStepShipmentFormInfo = {
	service: 'Parcel',
	optionalServices: [],
	optionalServicesNames: [],
	parcelCount: 1,
	dontKnowWeight: false,
	parcelWeight: 0,
	weightCategory: '',
	serviceOption: '',
	serviceName: '',
	serviceOptionPostbox: null,
	serviceOptionParcelpoint: null,
	parcelWidth: '',
	parcelHeight: '',
	parcelLength: '',
	price: '',

	codAmount: '',
	codSsn: '',
	codBank: '',
	codAccountType: '',
	codAccount: '',
	codReference: '',
	insuranceAmount: '',
	insuranceTerms: false,
	receiverPhone: '',
	receiverPhoneCountryCode: 'IS',
	senderSsnValid: true,
};

const yupFormSelectOption = {
	label: yup.string(),
	value: yup.string(),
};

const shipmentPhoneValidationMessage = (value?: string) =>
	isIcelandicShipmentPhone(value)
		? 'step-info.mobile-format'
		: isShipmentPhoneMissingInternationalPrefix(value)
		? 'step-info.phone-international-prefix'
		: 'step-info.phone-format';

const validateShipmentMobilePhone = function(value?: string) {
	const normalizedValue = value || '';

	return (
		(isIcelandicShipmentPhone(normalizedValue)
			? isValidIcelandicShipmentPhone(normalizedValue)
			: isValidShipmentPhone(normalizedValue)) ||
		this.createError({ message: shipmentPhoneValidationMessage(value) })
	);
};

const base = {
	service: yup.string().required('step-type.service-required-text'),
	optionalServices: yup.array().of(yup.string()),
	parcelCount: yup.number().when('service', {
		is: 'Parcel',
		then: yup.number().min(1, 'step-type.parcel-count-required-text'),
	}),
	weightCategory: yup.string(),
	parcelWeight: yup.number().required('step-shipment.parcel-weight'),
	serviceOption: yup.string().required('step-shipment.service-option'),
	serviceName: yup.string(),
	serviceOptionPostbox: yup
		.object()
		.nullable()
		.shape(yupFormSelectOption)
		.when('serviceOption', {
			is: 'Postbox',
			then: yup.object().nullable().shape(yupFormSelectOption).required('step-shipment.service-option-postbox'),
		}),
	serviceOptionParcelpoint: yup
		.object()
		.nullable()
		.shape(yupFormSelectOption)
		.when('serviceOption', {
			is: 'ParcelPoint',
			then: yup
				.object()
				.nullable()
				.shape(yupFormSelectOption)
				.required('step-shipment.service-option-parcelpoint'),
		}),
	parcelWidth: yup.string(),
	parcelHeight: yup.string(),
	parcelLength: yup.string(),
	dontKnowWeight: yup.boolean(),

	codAmount: yup.string().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup
			.string()
			.matches(/^[0-9]*$/, 'step-optional-services.only-numbers')
			.required('step-optional-services.amount-required'),
	}),
	codSsn: yup.string().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup.string().min(10).max(10, 'step-info.max').required('step-optional-services.ssn-required'),
	}),
	codBank: yup.string().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup.string().max(4, 'step-info.max').required('step-optional-services.bank-required'),
	}),
	codAccountType: yup.string().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup
			.string()
			.required('step-optional-services.account-type-required')
			.test('length', 'step-optional-services.account-type-length', val => val && val.length === 2),
	}),
	codAccount: yup.string().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup.string().max(6, 'step-info.max').required('step-optional-services.account-required'),
	}),
	codReference: yup.number().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_COD),
		then: yup
			.number()
			.max(9999999, 'step-optional-services.reference-length')
			.typeError('step-optional-services.reference-type'),
	}),
	insuranceAmount: yup.number().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_INSURANCE),
		then: yup
			.number()
			.required('step-optional-services.amount')
			.min(500, 'step-optional-services.amount-min')
			.max(3000000, 'step-optional-services.amount-max')
			.typeError('step-optional-services.amount-type'),
	}),
	insuranceTerms: yup.boolean().when('optionalServices', {
		is: optionalServices => optionalServices.includes(OPTIONAL_SERVICE_INSURANCE),
		then: yup.boolean().oneOf([true], 'required'),
	}),
	senderSsnValid: yup
		.boolean()
		.when('serviceOption', {
			is: 'PostOffice',
			then: yup.bool().oneOf([true], 'step-info.social-id-required'),
		})
		.when('serviceOption', {
			is: 'RuralParcel',
			then: yup.bool().oneOf([true], 'step-info.social-id-required'),
		})
		.when('serviceOption', {
			is: 'HomeDelivery',
			then: yup.bool().oneOf([true], 'step-info.social-id-required'),
		}),
};

export const StepShipmentSchema = yup.object().shape({ ...base });

export const StepShipmentSchema_ReceiverPhone = yup.object().shape({
	...base,
	receiverPhone: yup
		.string()
		.when('serviceOption', {
			is: 'ParcelPoint',
			then: yup
				.string()
				.required('required')
				.test('is-valid-shipment-mobile-phone', 'step-info.phone-format', validateShipmentMobilePhone),
		})
		.when('serviceOption', {
			is: 'Postbox',
			then: yup
				.string()
				.required('required')
				.test('is-valid-shipment-mobile-phone', 'step-info.phone-format', validateShipmentMobilePhone),
		}),
});
